// PIN gate wrapper. Wraps any personal-data page that should require
// PIN verification. Manager / admin / super_admin bypass automatically
// (they see admin pages instead).
//
// Usage:
//   <PinGate>
//     {(activeUser) => <MyHoursPage userId={activeUser.id} />}
//   </PinGate>

import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

export default function PinGate({ children, title }) {
  const { user, isAtLeastManager } = useAuth();

  // Managers bypass entirely — they see this data via admin pages
  const bypass = isAtLeastManager || user?.permission_tier === 'super_admin' || user?.permission_tier === 'admin';

  const [pin, setPin] = useState('');
  const [activeUser, setActiveUser] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Inactivity timer: auto-clear PIN session after 5 minutes idle
  useEffect(() => {
    if (!activeUser) return;
    const reset = () => {
      clearTimeout(window.__pinGateTimer);
      window.__pinGateTimer = setTimeout(() => {
        api.pinLogout().catch(() => {});
        localStorage.removeItem('pinSession');
        setActiveUser(null);
      }, 5 * 60 * 1000);
    };
    ['mousedown', 'keydown', 'touchstart'].forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      ['mousedown', 'keydown', 'touchstart'].forEach(e => window.removeEventListener(e, reset));
      clearTimeout(window.__pinGateTimer);
    };
  }, [activeUser]);

  if (bypass) {
    return children({ id: user.id, full_name: user.full_name, bypass: true });
  }

  if (activeUser) {
    return (
      <div className="page">
        <div className="pin-gate-active">
          <div className="muted">Viewing personal data for <strong>{activeUser.full_name}</strong></div>
          <button className="btn-secondary" onClick={() => {
            api.pinLogout().catch(() => {});
            localStorage.removeItem('pinSession');
            setActiveUser(null);
          }}>Done</button>
        </div>
        {children(activeUser)}
      </div>
    );
  }

  // Keypad
  function press(d) { if (busy) return; setErr(''); setPin(p => p.length >= 6 ? p : p + d); }
  function back() { setErr(''); setPin(p => p.slice(0, -1)); }
  function clear() { setErr(''); setPin(''); }

  async function submit(e) {
    e?.preventDefault?.();
    if (pin.length < 4) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api.pinLogin(pin);
      localStorage.setItem('pinSession', r.session_token);
      setActiveUser(r.active_user);
      setPin('');
    } catch (err) {
      setErr(err.message || 'Invalid PIN');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  // If logged in as a non-station individual (e.g., dispatcher with own
  // login), just show their own data without a PIN re-prompt.
  if (!user?.is_station_account) {
    return children({ id: user.id, full_name: user.full_name });
  }

  return (
    <div className="page pin-gate-page">
      <div className="page-header">
        <h2>{title || 'Personal'}</h2>
      </div>
      <div className="pin-gate-card">
        <div className="pin-gate-prompt">Enter your PIN to view your data</div>
        <form onSubmit={submit}>
          <div className="pin-dots">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className={`pin-dot ${i < pin.length ? 'pin-dot-filled' : ''}`} />
            ))}
          </div>
          {err && <div className="pin-entry-error">{err}</div>}
          <div className="pin-keypad" style={{ maxWidth: 280, margin: '12px auto' }}>
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} type="button" className="pin-key" onClick={() => press(String(n))}>{n}</button>
            ))}
            <button type="button" className="pin-key pin-key-aux" onClick={clear}>Clear</button>
            <button type="button" className="pin-key" onClick={() => press('0')}>0</button>
            <button type="button" className="pin-key pin-key-aux" onClick={back}>⌫</button>
          </div>
          <button type="submit" className="pin-submit-btn" disabled={pin.length < 4 || busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
